import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AlertTriangle, Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { Device, deviceService } from "@/services/deviceService";
import { cn } from "@/lib/utils";

interface BulkEmergencyStopDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  devices: Device[];
  onSuccess?: () => void;
}

export default function BulkEmergencyStopDialog({
  open,
  onOpenChange,
  devices,
  onSuccess,
}: BulkEmergencyStopDialogProps) {
  const [selectedCompany, setSelectedCompany] = useState<string>("all");
  const [companyOpen, setCompanyOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Benzersiz firmaları al
  const companies = useMemo(() => {
    const uniqueCompanies = new Map();
    devices.forEach(device => {
      if (device.company) {
        uniqueCompanies.set(device.company._id, device.company.name);
      }
    });
    return Array.from(uniqueCompanies.entries()).map(([id, name]) => ({ id, name }));
  }, [devices]);

  // Filtrelenmiş firmalar (arama için)
  const filteredCompanies = useMemo(() => {
    if (!searchQuery) return companies;
    return companies.filter(c =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [companies, searchQuery]);

  // Filtrelenmiş cihazlar
  const filteredDevices = useMemo(() => {
    const onlineDevices = devices.filter(d => d.isOnline);
    if (selectedCompany === "all") {
      return onlineDevices;
    }
    return onlineDevices.filter(d => d.company?._id === selectedCompany);
  }, [devices, selectedCompany]);

  const handleSubmit = async () => {
    if (filteredDevices.length === 0) {
      toast.error("Acil durduracak cihaz yok");
      return;
    }

    try {
      setLoading(true);
      const deviceIds = filteredDevices.map(d => d._id);
      await deviceService.emergencyStop(deviceIds);

      toast.success(`${filteredDevices.length} cihaz için acil durdurma komutu gönderildi`);
      onOpenChange(false);
      resetForm();
      onSuccess?.();
    } catch (error) {
      toast.error("Acil durdurma sırasında hata oluştu");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedCompany("all");
    setSearchQuery("");
  };

  const handleSelectCompany = (companyId: string) => {
    setSelectedCompany(companyId);
    setCompanyOpen(false);
    setSearchQuery("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Toplu Acil Durdur
          </DialogTitle>
        </DialogHeader>

        {/* Firma Seçimi */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Firma
          </Label>
          <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={companyOpen}
                className="w-full justify-between"
              >
                {selectedCompany === "all"
                  ? "Tüm Firmalar"
                  : companies.find((company) => company.id === selectedCompany)?.name || "Firma seçin"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <div className="flex flex-col">
                {/* Manuel Arama Input */}
                <div className="border-b px-3 py-2">
                  <Input
                    placeholder="Firma ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9"
                  />
                </div>

                {/* Firma Listesi */}
                <div className="max-h-64 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
                  {/* Tüm Firmalar */}
                  <div
                    className={cn(
                      "flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors",
                      selectedCompany === "all" && "bg-blue-50"
                    )}
                    onClick={() => handleSelectCompany("all")}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedCompany === "all" ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="text-sm">Tüm Firmalar</span>
                  </div>

                  {/* Filtrelenmiş Firmalar */}
                  {filteredCompanies.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                      Firma bulunamadı.
                    </div>
                  ) : (
                    filteredCompanies.map((company) => (
                      <div
                        key={company.id}
                        className={cn(
                          "flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors",
                          selectedCompany === company.id && "bg-blue-50"
                        )}
                        onClick={() => handleSelectCompany(company.id)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedCompany === company.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="text-sm">{company.name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Uyarı Banner */}
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-red-800">
            <p className="font-medium mb-1">
              {filteredDevices.length} çevrimiçi cihaz acil durdurulacak
            </p>
            <p>
              Bu işlem tüm seçilen cihazlardaki müzik çalmayı anında durduracaktır.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resetForm();
            }}
            disabled={loading}
          >
            İptal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={filteredDevices.length === 0 || loading}
            variant="destructive"
          >
            {loading ? "Durduruluyor..." : "Acil Durdur"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
